import { 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs,
  limit,
  Timestamp
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, auth } from '../firebaseConfig';
import { secureLog } from '../../lib/errors/logger';
import { isValidDocumentId, validateString, validateNumber } from '../../utils/validation';
import { sanitizeError } from '../../utils/sanitization';

// Trace log types
export type TraceLogType = 'CREATED' | 'UPDATED' | 'REPORTED' | 'REVIEWED' | 'SOLD' | 'DELETED' | 'RESTORED';
export type TraceLogRole = 'seller' | 'buyer' | 'admin' | 'system';

// Trace log interface
export interface TraceLog {
  id?: string;
  productId: string;
  type: TraceLogType;
  timestamp: Timestamp;
  by: string; // user ID or "system"
  role: TraceLogRole;
  note: string;
  metadata?: {
    orderId?: string;
    reportId?: string;
    reviewId?: string;
    changes?: Record<string, any>;
    [key: string]: any;
  };
}

// Trace service
export const traceService = {
  // Log a product trace event
  async logProductTrace(
    productId: string,
    type: TraceLogType,
    by: string,
    role: TraceLogRole,
    note: string,
    metadata?: TraceLog['metadata']
  ): Promise<string> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(productId)) {
        throw new Error('Invalid product ID');
      }

      if (!isValidDocumentId(by)) {
        throw new Error('Invalid user ID');
      }

      const noteValidation = validateString(note, 'Note', 1, 1000);
      if (!noteValidation.valid) {
        throw new Error(noteValidation.message || 'Invalid note');
      }

      // Security: IDOR Protection - Verify 'by' matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      if (by !== currentUser.uid && by !== 'system') {
        // SECURITY: Prevent IDOR - users can only log traces for themselves
        secureLog('warn', 'IDOR attempt detected in logProductTrace');
        throw new Error('Access denied');
      }

      // Security: Validate role
      const validRoles: TraceLogRole[] = ['seller', 'buyer', 'admin', 'system'];
      if (!validRoles.includes(role)) {
        throw new Error('Invalid role');
      }

      // Security: Validate type
      const validTypes: TraceLogType[] = ['CREATED', 'UPDATED', 'REPORTED', 'REVIEWED', 'SOLD', 'DELETED', 'RESTORED'];
      if (!validTypes.includes(type)) {
        throw new Error('Invalid trace log type');
      }

      const traceLogsCollection = collection(db, 'Products', productId, 'traceLogs');
      
      const traceLog: Omit<TraceLog, 'id'> = {
        productId,
        type,
        timestamp: serverTimestamp() as any,
        by: currentUser.uid, // Ensure 'by' matches authenticated user
        role,
        note: note.slice(0, 1000), // Limit note length
        metadata: metadata || undefined
      };

      const docRef = await addDoc(traceLogsCollection, traceLog);
      return docRef.id;
    } catch (error) {
      secureLog('warn', 'Failed to log product trace:', sanitizeError(error));
      throw new Error('Failed to log product trace');
    }
  },

  // Get trace logs for a product
  async getProductTraceLogs(
    productId: string, 
    limitCount: number = 50
  ): Promise<TraceLog[]> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(productId)) {
        return [];
      }

      const limitValidation = validateNumber(limitCount, 'Limit', 1, 100);
      if (!limitValidation.valid) {
        limitCount = 50; // Use default
      }

      const traceLogsCollection = collection(db, 'Products', productId, 'traceLogs');
      const q = query(
        traceLogsCollection,
        orderBy('timestamp', 'desc'),
        limit(Math.min(limitCount, 100))
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as TraceLog[];
    } catch (error) {
      secureLog('warn', 'Failed to get product trace logs:', sanitizeError(error));
      return [];
    }
  },

  // Get trace logs by type
  async getProductTraceLogsByType(
    productId: string,
    type: TraceLogType,
    limitCount: number = 20
  ): Promise<TraceLog[]> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(productId)) {
        return [];
      }

      const validTypes: TraceLogType[] = ['CREATED', 'UPDATED', 'REPORTED', 'REVIEWED', 'SOLD', 'DELETED', 'RESTORED'];
      if (!validTypes.includes(type)) {
        return [];
      }

      const limitValidation = validateNumber(limitCount, 'Limit', 1, 100);
      if (!limitValidation.valid) {
        limitCount = 20; // Use default
      }

      const traceLogsCollection = collection(db, 'Products', productId, 'traceLogs');
      const q = query(
        traceLogsCollection,
        where('type', '==', type),
        orderBy('timestamp', 'desc'),
        limit(Math.min(limitCount, 100))
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as TraceLog[];
    } catch (error) {
      secureLog('warn', 'Failed to get product trace logs by type:', sanitizeError(error));
      return [];
    }
  },

  // Get recent trace logs across all products (admin only)
  async getRecentTraceLogs(limitCount: number = 100): Promise<TraceLog[]> {
    try {
      // Security: Input validation
      const limitValidation = validateNumber(limitCount, 'Limit', 1, 500);
      if (!limitValidation.valid) {
        limitCount = 100; // Use default
      }

      // Security: Admin check - this should be done server-side, but add client-side check
      const currentUser = auth.currentUser;
      if (!currentUser) {
        secureLog('warn', 'Unauthenticated access attempt to getRecentTraceLogs');
        return [];
      }

      // This would require a composite index or different approach
      // For now, we'll get logs from the most recent products
      const productsCollection = collection(db, 'Products');
      const productsQuery = query(
        productsCollection,
        orderBy('updatedAt', 'desc'),
        limit(10)
      );
      
      const productsSnapshot = await getDocs(productsQuery);
      const allLogs: TraceLog[] = [];

      for (const productDoc of productsSnapshot.docs) {
        const productLogs = await this.getProductTraceLogs(productDoc.id, 10);
        allLogs.push(...productLogs);
      }

      // Sort by timestamp and limit
      return allLogs
        .sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis())
        .slice(0, Math.min(limitCount, 500));
    } catch (error) {
      secureLog('warn', 'Failed to get recent trace logs:', sanitizeError(error));
      return [];
    }
  },

  // Check if product is trusted (has verified review and no reports)
  async isProductTrusted(productId: string): Promise<boolean> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(productId)) {
        return false;
      }

      const [reviewedLogs, reportedLogs] = await Promise.all([
        this.getProductTraceLogsByType(productId, 'REVIEWED'),
        this.getProductTraceLogsByType(productId, 'REPORTED')
      ]);

      // Check if there's a verified review
      const hasVerifiedReview = reviewedLogs.some(log => 
        log.note.toLowerCase().includes('verified') || 
        log.note.toLowerCase().includes('trusted')
      );

      // Check if there are no reports
      const hasNoReports = reportedLogs.length === 0;

      return hasVerifiedReview && hasNoReports;
    } catch (error) {
      secureLog('warn', 'Failed to check if product is trusted:', sanitizeError(error));
      return false;
    }
  },

  // Get trace statistics for a product
  async getProductTraceStats(productId: string): Promise<{
    totalLogs: number;
    byType: Record<TraceLogType, number>;
    byRole: Record<TraceLogRole, number>;
    lastActivity: Date | null;
  }> {
    try {
      // Security: Input validation
      if (!isValidDocumentId(productId)) {
        return {
          totalLogs: 0,
          byType: {
            CREATED: 0,
            UPDATED: 0,
            REPORTED: 0,
            REVIEWED: 0,
            SOLD: 0,
            DELETED: 0,
            RESTORED: 0
          },
          byRole: {
            seller: 0,
            buyer: 0,
            admin: 0,
            system: 0
          },
          lastActivity: null
        };
      }

      const logs = await this.getProductTraceLogs(productId, 1000);
      
      const byType: Record<TraceLogType, number> = {
        CREATED: 0,
        UPDATED: 0,
        REPORTED: 0,
        REVIEWED: 0,
        SOLD: 0,
        DELETED: 0,
        RESTORED: 0
      };

      const byRole: Record<TraceLogRole, number> = {
        seller: 0,
        buyer: 0,
        admin: 0,
        system: 0
      };

      let lastActivity: Date | null = null;

      logs.forEach(log => {
        byType[log.type]++;
        byRole[log.role]++;
        
        const logDate = log.timestamp.toDate();
        if (!lastActivity || logDate > lastActivity) {
          lastActivity = logDate;
        }
      });

      return {
        totalLogs: logs.length,
        byType,
        byRole,
        lastActivity
      };
    } catch (error) {
      secureLog('warn', 'Failed to get product trace stats:', sanitizeError(error));
      return {
        totalLogs: 0,
        byType: {
          CREATED: 0,
          UPDATED: 0,
          REPORTED: 0,
          REVIEWED: 0,
          SOLD: 0,
          DELETED: 0,
          RESTORED: 0
        },
        byRole: {
          seller: 0,
          buyer: 0,
          admin: 0,
          system: 0
        },
        lastActivity: null
      };
    }
  }
};
